import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import TrustUserTicketStatus from './trustUserTicketStatus';
import TrustAdminLogin from './TrustAdminLogin';
import MyContacts from './MyContacts';
import { downloadRewardCardImage } from './RewardShareCard';

const UtilityDashboard = () => {
  const navigate = useNavigate();

  // Validate session presence and token expiration
  React.useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (!userStr) {
      navigate("/login");
      return;
    }
    try {
      const userObj = JSON.parse(userStr);
      if (userObj && userObj.exp) {
        const currentTime = Date.now() / 1000;
        if (userObj.exp < currentTime) {
          console.warn("Session expired. Clearing storage and redirecting to login.");
          localStorage.removeItem("user");
          localStorage.removeItem("userMobile");
          navigate("/login");
          return;
        }
      }
    } catch (e) {
      console.error("Invalid user session. Clearing storage and redirecting to login.", e);
      localStorage.removeItem("user");
      localStorage.removeItem("userMobile");
      navigate("/login");
    }
  }, [navigate]);

  // Get current user email from localStorage
  const userStr = localStorage.getItem("user");
  let userEmail = "";
  let userName = "";
  if (userStr) {
    try {
      const userObj = JSON.parse(userStr);
      userEmail = userObj?.email || "";
      userName = userObj?.name || userObj?.userName || "";
    } catch (e) {
      console.error("Failed to parse user from localStorage", e);
    }
  }

  const isCloudAiContact = userEmail === 'contact@cloudai.in';
  const utilityItems = [
    { name: 'My Contacts', icon: '👥', viewKey: 'contacts' },
    ...(isCloudAiContact
      ? [
        { name: 'Mobile Recharge', path: '/mobile-recharge', icon: '📱' },
        { name: 'Investment tips', path: '/trading-tips', icon: '📈' },
      ]
      : []),
    {
      name: 'Tools',
      openKey: 'tools',
      icon: '🛠️',
      children: [
        { name: 'Broadcast', icon: '📢', viewKey: 'broadcast', disabled: false },
        { name: 'Any Trips', icon: '🛣️', disabled: true },
        { name: 'Any Delivery', icon: '🚚', disabled: true },
      ],
    },
    {
      name: 'Profile',
      openKey: 'profile',
      icon: '🤝',
      children: [
        { name: 'Client', path: '/trustapp/client', icon: '🧑', disabled: true },
        { name: 'Service Provider', path: '/trustapp/service-provider', icon: '🔧', disabled: true },
      ],
    },
    // Added "Pay us" option
    { name: 'Pay us', path: '/pay-us', icon: '💳' },
    { name: 'My Rewards', icon: '₹', viewKey: 'rewards' },
    { name: 'My Tickets', icon: '🎫', viewKey: 'tickets' },
    { name: 'Customer Support', icon: '🎧', viewKey: 'customer-support' },
  ];

  if (userEmail === 'contact@cloudai.in' || userEmail === 'sonuojha83@gmail.com') {
    utilityItems.push({ name: 'Admin', icon: '🛡️', viewKey: 'admin' });
  }

  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [openMenu, setOpenMenu] = React.useState(null);
  const [activeView, setActiveView] = React.useState('home');
  const [broadcastPlanId, setBroadcastPlanId] = React.useState('');
  const [broadcastType, setBroadcastType] = React.useState('');
  const [broadcastMessage, setBroadcastMessage] = React.useState('');
  const [broadcastLatitude, setBroadcastLatitude] = React.useState('');
  const [broadcastLongitude, setBroadcastLongitude] = React.useState('');
  const [broadcastLocationStatus, setBroadcastLocationStatus] = React.useState('');
  const [broadcastStatus, setBroadcastStatus] = React.useState(null);
  const [broadcastSubmitting, setBroadcastSubmitting] = React.useState(false);
  const [broadcastRegistrationComplete, setBroadcastRegistrationComplete] = React.useState(false);
  const [registrationMobile, setRegistrationMobile] = React.useState('');
  const [registrationOtp, setRegistrationOtp] = React.useState('');
  const [registrationStep, setRegistrationStep] = React.useState('mobile');
  const [registrationStatus, setRegistrationStatus] = React.useState(null);
  const [registrationSubmitting, setRegistrationSubmitting] = React.useState(false);
  const [broadcastAccessChecking, setBroadcastAccessChecking] = React.useState(false);

  const broadcastTypes = [
    { label: 'Rent', planId: 8 },
    { label: 'Hot Offer', planId: 9 },
    { label: 'Hiring', planId: 10 },
    { label: 'Need job', planId: 11 },
    { label: 'Buy', planId: 12 },
    { label: 'Sell', planId: 13 },
  ];

  const handleBroadcastLocation = () => {
    if (!navigator.geolocation) {
      setBroadcastLocationStatus('Location is not supported by this browser.');
      return;
    }

    setBroadcastLocationStatus('Fetching precise location...');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setBroadcastLatitude(position.coords.latitude.toFixed(7));
        setBroadcastLongitude(position.coords.longitude.toFixed(7));
        setBroadcastLocationStatus('Location captured.');
      },
      (error) => {
        const message = error.code === error.PERMISSION_DENIED
          ? 'Location permission was denied.'
          : 'Unable to fetch your location. Please try again.';
        setBroadcastLocationStatus(message);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  // ── Ticket form state ──
  const [ticketMobile, setTicketMobile] = React.useState('');
  const [ticketSubject, setTicketSubject] = React.useState('');
  const [ticketDetails, setTicketDetails] = React.useState('');
  const [ticketErrors, setTicketErrors] = React.useState({});
  const [ticketSubmitted, setTicketSubmitted] = React.useState(false);
  const [ticketSubmitting, setTicketSubmitting] = React.useState(false);
  const [ticketTopic, setTicketTopic] = React.useState('');
  const [ticketMobileLocked, setTicketMobileLocked] = React.useState(false);
  const [ticketMobileLookupError, setTicketMobileLookupError] = React.useState('');
  const [ticketRegistrationStep, setTicketRegistrationStep] = React.useState('mobile');
  const [ticketRegistrationOtp, setTicketRegistrationOtp] = React.useState('');
  const [ticketRegistrationStatus, setTicketRegistrationStatus] = React.useState(null);
  const [ticketRegistrationSubmitting, setTicketRegistrationSubmitting] = React.useState(false);
  const [rewardTotal, setRewardTotal] = React.useState(0);
  const [rewardLoading, setRewardLoading] = React.useState(false);
  const [rewardError, setRewardError] = React.useState('');
  const [rewardDownloadStatus, setRewardDownloadStatus] = React.useState('');
  const [rewardDownloading, setRewardDownloading] = React.useState(false);

  React.useEffect(() => {
    if (activeView !== 'customer-support' || !userEmail) return;

    let isActive = true;
    setTicketMobileLocked(false);
    setTicketRegistrationStep('mobile');
    setTicketRegistrationOtp('');
    setTicketRegistrationStatus(null);

    const loadTicketMobile = async () => {
      setTicketMobileLookupError('');
      try {
        const response = await apiClient.post('/api/v2/contacts/get-mobile-by-email', {
          email: userEmail,
        });
        const mappedMobile = String(
          response.data?.mobile
          || response.data?.mobileNumber
          || response.data?.data?.mobile
          || response.data?.data?.mobileNumber
          || response.data
          || ''
        ).replace(/\D/g, '');

        if (isActive && /^\d{10}$/.test(mappedMobile)) {
          localStorage.setItem('userMobile', mappedMobile);
          setTicketMobile(mappedMobile);
          setTicketMobileLocked(true);
          setTicketErrors((currentErrors) => ({ ...currentErrors, mobile: undefined }));
          return;
        }

        if (isActive) {
          setTicketMobile('');
          setTicketMobileLocked(false);
          setTicketMobileLookupError('No verified mobile number is linked to this account.');
        }
      } catch (error) {
        if (isActive) {
          setTicketMobile('');
          setTicketMobileLocked(false);
          setTicketMobileLookupError('We could not verify a mobile number for this account.');
        }
        console.error('Ticket mobile lookup error:', error);
      }
    };

    loadTicketMobile();

    return () => {
      isActive = false;
    };
  }, [activeView, userEmail]);

  const handleRewardImageDownload = async () => {
    setRewardDownloadStatus('');
    setRewardDownloading(true);

    try {
      await downloadRewardCardImage(rewardTotal);
      setRewardDownloadStatus('Reward image downloaded successfully.');
    } catch (error) {
      console.error('Reward image download error:', error);
      setRewardDownloadStatus('Unable to download the reward image. Please try again.');
    } finally {
      setRewardDownloading(false);
    }
  };

  React.useEffect(() => {
    if (activeView !== 'rewards') return;

    const loadRewards = async () => {
      setRewardLoading(true);
      setRewardError('');
      try {
        let mobile = (localStorage.getItem('userMobile') || '').replace(/\D/g, '');
        if (!/^\d{10}$/.test(mobile) && userEmail) {
          const mobileResponse = await apiClient.post('/api/v2/contacts/get-mobile-by-email', {
            email: userEmail,
          });
          mobile = String(mobileResponse.data || '').replace(/\D/g, '');
          if (/^\d{10}$/.test(mobile)) localStorage.setItem('userMobile', mobile);
        }

        if (!/^\d{10}$/.test(mobile)) {
          setRewardError('No mobile number is linked to this account yet.');
          return;
        }

        const response = await apiClient.get(`/v1/rewards/${mobile}`);
        setRewardTotal(Number(response.data?.totalRewards || 0));
      } catch (error) {
        console.error('Rewards load error:', error);
        setRewardError(error.response?.data?.msg || 'Unable to load your rewards. Please try again.');
      } finally {
        setRewardLoading(false);
      }
    };

    loadRewards();
  }, [activeView, userEmail]);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("userMobile");
    navigate('/login');
  };

  const requestProfileOtp = async (mobile) => {
    const response = await apiClient.post('/api/v2/trust/user/send-otp', { mobile });
    if (response.data?.status === false) {
      throw new Error(response.data?.msg || 'Unable to send OTP. Please try again.');
    }
    return response.data;
  };

  const mobileMappedToAnotherEmailMessage = 'Mobile mapped with some other email, please enter another mobile number.';

  const verifyOtpAndEnsureProfile = async (mobile, otp) => {
    const verifyResponse = await apiClient.post('/api/v2/trust/user/verify-otp', { mobile, otp });
    if (verifyResponse.data?.status === false) {
      throw new Error(verifyResponse.data?.msg || 'OTP verification failed.');
    }

    try {
      const profileLookupResponse = await apiClient.post('/api/v2/contacts/get-gmail-by-mobile', {
        mobileNumber: mobile,
      });
      const mappedEmail = String(profileLookupResponse.data || '').trim().toLowerCase();

      if (mappedEmail) {
        if (mappedEmail !== userEmail.toLowerCase()) {
          throw new Error(mobileMappedToAnotherEmailMessage);
        }

        return { msg: 'Mobile number verified. Existing profile found.' };
      }
    } catch (error) {
      if (error.message === mobileMappedToAnotherEmailMessage) {
        throw error;
      }
      if (error.response?.status !== 404 && error.response?.status !== 204) {
        throw error;
      }
    }

    const profileResponse = await apiClient.post('/api/v2/trust/user/profile', {
      mobile,
      email: userEmail,
      name: userName || userEmail,
    });
    if (profileResponse.data?.status === false) {
      throw new Error(profileResponse.data?.msg || 'Profile creation failed.');
    }
    return profileResponse.data;
  };

  const handleSendRegistrationOtp = async (event) => {
    event.preventDefault();
    const mobile = registrationMobile.replace(/\D/g, '');
    if (!/^\d{10}$/.test(mobile)) {
      setRegistrationStatus({ type: 'error', message: 'Please enter a valid 10-digit mobile number.' });
      return;
    }

    setRegistrationSubmitting(true);
    setRegistrationStatus(null);
    try {
      const responseData = await requestProfileOtp(mobile);

      setRegistrationMobile(mobile);
      setRegistrationStep('otp');
      setRegistrationStatus({
        type: 'success',
        message: responseData?.msg || 'OTP sent to your mobile number.',
      });
    } catch (error) {
      setRegistrationStatus({
        type: 'error',
        message: error.response?.data?.msg || 'Unable to send OTP. Please try again.',
      });
    } finally {
      setRegistrationSubmitting(false);
    }
  };

  const handleVerifyRegistrationOtp = async (event) => {
    event.preventDefault();
    const otp = registrationOtp.trim();
    if (!/^\d{4,6}$/.test(otp)) {
      setRegistrationStatus({ type: 'error', message: 'Please enter the OTP sent to your mobile.' });
      return;
    }

    setRegistrationSubmitting(true);
    setRegistrationStatus(null);
    try {
      const profileData = await verifyOtpAndEnsureProfile(registrationMobile, otp);

      localStorage.setItem('userMobile', registrationMobile);
      setBroadcastRegistrationComplete(true);
      setRegistrationStatus({
        type: 'success',
        message: profileData?.msg || 'Profile is ready.',
      });
    } catch (error) {
      if (error.message === mobileMappedToAnotherEmailMessage) {
        setRegistrationStep('mobile');
        setRegistrationOtp('');
      }
      setRegistrationStatus({
        type: 'error',
        message: error.response?.data?.msg || error.message || 'Unable to verify OTP. Please try again.',
      });
    } finally {
      setRegistrationSubmitting(false);
    }
  };

  const handleSendTicketRegistrationOtp = async (event) => {
    event.preventDefault();
    const mobile = ticketMobile.replace(/\D/g, '');
    if (!/^\d{10}$/.test(mobile)) {
      setTicketRegistrationStatus({ type: 'error', message: 'Please enter a valid 10-digit mobile number.' });
      return;
    }

    setTicketRegistrationSubmitting(true);
    setTicketRegistrationStatus(null);
    try {
      const responseData = await requestProfileOtp(mobile);
      setTicketMobile(mobile);
      setTicketRegistrationStep('otp');
      setTicketRegistrationStatus({
        type: 'success',
        message: responseData?.msg || 'OTP sent to your mobile number.',
      });
    } catch (error) {
      setTicketRegistrationStatus({
        type: 'error',
        message: error.response?.data?.msg || error.message || 'Unable to send OTP. Please try again.',
      });
    } finally {
      setTicketRegistrationSubmitting(false);
    }
  };

  const handleVerifyTicketRegistrationOtp = async (event) => {
    event.preventDefault();
    const otp = ticketRegistrationOtp.trim();
    if (!/^\d{4,6}$/.test(otp)) {
      setTicketRegistrationStatus({ type: 'error', message: 'Please enter the OTP sent to your mobile.' });
      return;
    }

    setTicketRegistrationSubmitting(true);
    setTicketRegistrationStatus(null);
    try {
      const profileData = await verifyOtpAndEnsureProfile(ticketMobile, otp);
      localStorage.setItem('userMobile', ticketMobile);
      setTicketMobileLocked(true);
      setTicketRegistrationStatus({
        type: 'success',
        message: profileData?.msg || 'Profile is ready.',
      });
    } catch (error) {
      if (error.message === mobileMappedToAnotherEmailMessage) {
        setTicketRegistrationStep('mobile');
        setTicketRegistrationOtp('');
      }
      setTicketRegistrationStatus({
        type: 'error',
        message: error.response?.data?.msg || error.message || 'Unable to verify OTP. Please try again.',
      });
    } finally {
      setTicketRegistrationSubmitting(false);
    }
  };

  const handleBroadcastSelection = async () => {
    setActiveView('broadcast');
    setBroadcastRegistrationComplete(false);
    setRegistrationStep('mobile');
    setRegistrationStatus(null);
    setRegistrationOtp('');

    if (!userEmail) {
      setRegistrationStatus({ type: 'error', message: 'Your logged-in email could not be found. Please log in again.' });
      setActiveView('broadcast');
      return;
    }

    setBroadcastAccessChecking(true);
    try {
      const response = await apiClient.post('/api/v2/contacts/get-mobile-by-email', {
        email: userEmail,
      });
      const registeredMobile = String(response.data || '').trim();

      if (registeredMobile && /^\d{10}$/.test(registeredMobile)) {
        setRegistrationMobile(registeredMobile);
        localStorage.setItem('userMobile', registeredMobile);
        setBroadcastRegistrationComplete(true);
      } else {
        setActiveView('broadcast');
      }
    } catch (error) {
      if (error.response?.status === 204) {
        setActiveView('broadcast');
      } else {
        setRegistrationStatus({
          type: 'error',
          message: error.response?.data?.msg || 'Unable to check your TrustApp profile. Please try again.',
        });
        setActiveView('broadcast');
      }
    } finally {
      setBroadcastAccessChecking(false);
    }
  };

  // ── Sidebar width ──
  const sidebarWidth = isMobile ? '240px' : '260px';

  return (
    <div style={{
      fontFamily: 'Inter, sans-serif',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
    }}>

      {/* ═══ Top Header Bar ═══ */}
      <header style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: isMobile ? '12px 16px' : '14px 32px',
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        zIndex: 200,
        position: 'sticky',
        top: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Hamburger for mobile */}
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                padding: '4px',
                lineHeight: 1,
                color: '#333',
              }}
              aria-label="Toggle menu"
            >☰</button>
          )}
        </div>

        <button
          onClick={handleLogout}
          style={{
            padding: isMobile ? '6px 14px' : '8px 22px',
            backgroundColor: '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: isMobile ? '0.8rem' : '0.95rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background-color 0.2s, transform 0.15s',
            boxShadow: '0 2px 6px rgba(244,67,54,0.3)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#d32f2f';
            e.currentTarget.style.transform = 'scale(1.03)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#f44336';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >Logout</button>
      </header>

      {/* ═══ Body: Sidebar + Main Content ═══ */}
      <div style={{ display: 'flex', flex: 1, position: 'relative' }}>

        {/* ── Mobile overlay backdrop ── */}
        {isMobile && sidebarOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.4)',
              zIndex: 299,
            }}
          />
        )}

        {/* ── Left Sidebar ── */}
        <nav style={{
          width: sidebarWidth,
          minWidth: sidebarWidth,
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(12px)',
          borderRight: '1px solid rgba(0,0,0,0.06)',
          padding: '24px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          boxShadow: '2px 0 16px rgba(0,0,0,0.04)',
          // Mobile: slide-in overlay
          ...(isMobile ? {
            position: 'fixed',
            top: 0,
            left: sidebarOpen ? '0' : `-${sidebarWidth}`,
            height: '100vh',
            zIndex: 300,
            transition: 'left 0.3s ease',
            paddingTop: '70px',
          } : {}),
        }}>
          <div style={{
            padding: '0 20px 16px',
            fontSize: '0.7rem',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            color: '#9e9e9e',
          }}>Menu</div>

          {utilityItems.map((item) => {
            const isDisabled = item.disabled;
            const hasChildren = item.children && item.children.length > 0;

            // ── Parent item with sub-menu ──
            if (hasChildren) {
              const menuOpen = openMenu === (item.openKey || item.name);
              return (
                <div key={item.name}>
                  <div
                    onClick={() => setOpenMenu(menuOpen ? null : (item.openKey || item.name))}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      padding: '14px 24px',
                      margin: '0 12px',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: menuOpen ? '#e3f2fd' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e3f2fd';
                      e.currentTarget.style.transform = 'translateX(4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = menuOpen ? '#e3f2fd' : 'transparent';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                  >
                    <span style={{ fontSize: '1.4rem', flexShrink: 0 }}>{item.icon}</span>
                    <span style={{
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      color: '#333',
                      whiteSpace: 'nowrap',
                    }}>{item.name}</span>
                    <span style={{
                      marginLeft: 'auto',
                      fontSize: '0.7rem',
                      transition: 'transform 0.3s ease',
                      transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      color: '#666',
                    }}>▼</span>
                  </div>

                  {/* Sub-menu items */}
                  <div style={{
                    overflow: 'hidden',
                    maxHeight: menuOpen ? `${item.children.length * 52}px` : '0',
                    transition: 'max-height 0.3s ease',
                  }}>
                    {item.children.map((child) => {
                      const childDisabled = child.disabled;
                      return (
                        <div
                          key={child.name}
                          onClick={() => {
                            if (!childDisabled) {
                              if (child.viewKey) {
                                if (child.viewKey === 'broadcast') {
                                  handleBroadcastSelection();
                                } else {
                                  setActiveView(child.viewKey);
                                }
                              } else {
                                navigate(child.path);
                              }
                              if (isMobile) setSidebarOpen(false);
                            }
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '11px 24px 11px 44px',
                            margin: '0 12px',
                            borderRadius: '8px',
                            cursor: childDisabled ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            backgroundColor: 'transparent',
                            opacity: childDisabled ? 0.45 : 1,
                          }}
                          onMouseEnter={(e) => {
                            if (!childDisabled) {
                              e.currentTarget.style.backgroundColor = '#e8f5e9';
                              e.currentTarget.style.transform = 'translateX(4px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.transform = 'translateX(0)';
                          }}
                        >
                          <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{child.icon}</span>
                          <span style={{
                            fontSize: '0.88rem',
                            fontWeight: '500',
                            color: childDisabled ? '#aaa' : '#444',
                            whiteSpace: 'nowrap',
                          }}>{child.name}</span>

                          {childDisabled && (
                            <span style={{
                              marginLeft: 'auto',
                              backgroundColor: '#bdbdbd',
                              color: 'white',
                              fontSize: '0.5rem',
                              fontWeight: '700',
                              padding: '2px 6px',
                              borderRadius: '7px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.4px',
                              flexShrink: 0,
                            }}>Soon</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            }

            // ── Regular item ──
            return (
              <div
                key={item.name}
                onClick={() => {
                  if (!isDisabled) {
                    if (item.viewKey) {
                      setActiveView(item.viewKey);
                    } else {
                      navigate(item.path);
                    }
                    if (isMobile) setSidebarOpen(false);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '14px 24px',
                  margin: '0 12px',
                  borderRadius: '10px',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: isDisabled ? 0.45 : 1,
                  backgroundColor: 'transparent',
                  position: 'relative',
                }}
                onMouseEnter={(e) => {
                  if (!isDisabled) {
                    e.currentTarget.style.backgroundColor = '#e3f2fd';
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <span style={{ fontSize: '1.4rem', flexShrink: 0 }}>{item.icon}</span>
                <span style={{
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  color: isDisabled ? '#aaa' : '#333',
                  whiteSpace: 'nowrap',
                }}>{item.name}</span>

                {isDisabled && (
                  <span style={{
                    marginLeft: 'auto',
                    backgroundColor: '#bdbdbd',
                    color: 'white',
                    fontSize: '0.55rem',
                    fontWeight: '700',
                    padding: '2px 7px',
                    borderRadius: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                    flexShrink: 0,
                  }}>Soon</span>
                )}
              </div>
            );
          })}
        </nav>

        {/* ── Main Content Area ── */}
        <main style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: isMobile ? 'flex-start' : 'center',
          padding: isMobile ? '30px 16px' : '60px 40px',
          overflowY: 'auto',
        }}>
          {activeView === 'home' && (
            <div style={{
              background: 'rgba(255,255,255,0.85)',
              backdropFilter: 'blur(14px)',
              borderRadius: '20px',
              padding: isMobile ? '36px 24px' : '50px 48px',
              maxWidth: '620px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              border: '1px solid rgba(255,255,255,0.6)',
            }}>
              <div style={{ fontSize: '3.5rem', marginBottom: '16px' }}>🔒</div>
              <h2 style={{
                color: '#1565c0',
                fontSize: isMobile ? '1.4rem' : '1.7rem',
                fontWeight: '700',
                marginBottom: '18px',
                marginTop: 0,
              }}>Your Privacy Matters</h2>
              <p style={{
                color: '#546e7a',
                fontSize: '1rem',
                lineHeight: '1.75',
                fontStyle: 'italic',
                fontWeight: '500',
                margin: 0,
              }}>
                Your activities and personal data are completely private to you.
                All information is stored securely in our system in a uniquely encrypted form
                so that our system cannot read it, ensuring your privacy and safety at all times.
              </p>
            </div>
          )}

          {activeView === 'rewards' && (
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              padding: isMobile ? '30px 20px' : '42px 36px',
              maxWidth: '540px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.08)',
              border: '1px solid #000000',
            }}>
              <div style={{
                width: '72px',
                height: '72px',
                margin: '0 auto 16px',
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #fef3c7, #f59e0b)',
                color: '#78350f',
                fontSize: '2.4rem',
                fontWeight: '800',
                boxShadow: '0 8px 18px rgba(245, 158, 11, 0.25)',
              }}>₹</div>
              <p style={{ color: '#64748b', fontSize: '0.92rem', lineHeight: 1.6, margin: '0 0 10px' }}>
                Based on your activity in TrustApp, you have earned ₹{rewardTotal}.
              </p>
              <h2 style={{ margin: 0, color: '#0f172a', fontSize: isMobile ? '1.35rem' : '1.5rem' }}>
                My Rewards
              </h2>
              {rewardLoading ? (
                <p style={{ color: '#2563eb', fontWeight: '700' }}>Loading your rewards...</p>
              ) : rewardError ? (
                <p style={{ color: '#b91c1c', fontWeight: '600' }}>{rewardError}</p>
              ) : (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '14px 24px',
                  borderRadius: '14px',
                  backgroundColor: '#fffbeb',
                  color: '#92400e',
                  fontSize: '1.8rem',
                  fontWeight: '800',
                }}>
                  <span>₹</span>
                  <span>{rewardTotal}</span>
                </div>
              )}
              <p style={{ color: '#64748b', fontSize: isMobile ? '0.78rem' : '0.82rem', lineHeight: 1.5, margin: '10px auto 0', maxWidth: '440px', whiteSpace: 'normal', overflowWrap: 'break-word' }}>
                You can only withdraw ₹50 or more. To withdraw your earnings, please submit a ticket with PhonePe mobile number through Contact Support.
              </p>
              {!rewardLoading && !rewardError && (
                <button
                  type="button"
                  onClick={handleRewardImageDownload}
                  disabled={rewardDownloading}
                  style={{
                    display: 'inline-block',
                    marginTop: '18px',
                    padding: '11px 18px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                    color: '#ffffff',
                    fontWeight: '700',
                    border: 'none',
                    cursor: rewardDownloading ? 'wait' : 'pointer',
                    opacity: rewardDownloading ? 0.7 : 1,
                  }}
                >
                  {rewardDownloading ? 'Downloading reward image...' : 'Download reward image'}
                </button>
              )}
              {rewardDownloadStatus && (
                <p role="status" style={{ color: '#475569', fontSize: '0.82rem', lineHeight: 1.5, margin: '12px 0 0' }}>
                  {rewardDownloadStatus}
                </p>
              )}
              <p style={{ color: '#94a3b8', fontSize: '0.8rem', lineHeight: 1.5, margin: '18px 0 0' }}>
                Please note that TrustApp reserves the right to approve or deny withdrawal requests to prevent misuse of this feature.
              </p>
            </div>
          )}

          {activeView === 'tickets' && <TrustUserTicketStatus />}

          {activeView === 'admin' && <TrustAdminLogin />}

          {activeView === 'contacts' && <MyContacts embedded />}

          {activeView === 'broadcast' && !broadcastRegistrationComplete && (
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              padding: isMobile ? '28px 20px' : '36px',
              maxWidth: '540px',
              width: '100%',
              boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.08)',
              border: '1px solid #000000',
            }}>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>📢</div>
                <h2 style={{ margin: 0, color: '#0f172a', fontSize: '1.35rem' }}>
                  Create your TrustApp profile
                </h2>
                <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.5 }}>
                  You are not a registered user so we need to create your Profile in TrustApp
                </p>
              </div>

              {broadcastAccessChecking ? (
                <p style={{ textAlign: 'center', color: '#475569', fontSize: '0.9rem' }}>
                  Checking your TrustApp profile...
                </p>
              ) : (
              registrationStep === 'mobile' ? (
                <form onSubmit={handleSendRegistrationOtp}>
                  <label style={{ display: 'block', marginBottom: '18px', color: '#334155', fontSize: '0.85rem', fontWeight: '600' }}>
                    Mobile number
                    <input
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={registrationMobile}
                      onChange={(event) => setRegistrationMobile(event.target.value.replace(/\D/g, ''))}
                      placeholder="Enter 10-digit mobile number"
                      style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '6px', padding: '10px 12px', border: '1px solid #000000', borderRadius: '8px' }}
                    />
                  </label>
                  {registrationStatus && (
                    <p style={{ color: registrationStatus.type === 'success' ? '#15803d' : '#b91c1c', fontSize: '0.875rem' }}>
                      {registrationStatus.message}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={registrationSubmitting}
                    style={{ width: '100%', padding: '11px', border: '1px solid #000000', borderRadius: '8px', backgroundColor: '#2563eb', color: '#ffffff', fontWeight: '600', cursor: registrationSubmitting ? 'not-allowed' : 'pointer', opacity: registrationSubmitting ? 0.7 : 1 }}
                  >
                    {registrationSubmitting ? 'Sending OTP...' : 'Send OTP'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerifyRegistrationOtp}>
                  <label style={{ display: 'block', marginBottom: '18px', color: '#334155', fontSize: '0.85rem', fontWeight: '600' }}>
                    OTP
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={registrationOtp}
                      onChange={(event) => setRegistrationOtp(event.target.value.replace(/\D/g, ''))}
                      placeholder="Enter OTP"
                      style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '6px', padding: '10px 12px', border: '1px solid #000000', borderRadius: '8px' }}
                    />
                  </label>
                  {registrationStatus && (
                    <p style={{ color: registrationStatus.type === 'success' ? '#15803d' : '#b91c1c', fontSize: '0.875rem' }}>
                      {registrationStatus.message}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={registrationSubmitting}
                    style={{ width: '100%', padding: '11px', border: '1px solid #000000', borderRadius: '8px', backgroundColor: '#2563eb', color: '#ffffff', fontWeight: '600', cursor: registrationSubmitting ? 'not-allowed' : 'pointer', opacity: registrationSubmitting ? 0.7 : 1 }}
                  >
                    {registrationSubmitting ? 'Verifying...' : 'Verify OTP and Submit'}
                  </button>
                </form>
              )
              )}
            </div>
          )}

          {activeView === 'broadcast' && broadcastRegistrationComplete && (
            <div style={{
              background: 'linear-gradient(145deg, #ffffff 0%, #f8fbff 100%)',
              borderRadius: '24px',
              padding: isMobile ? '24px 18px' : '38px',
              maxWidth: '600px',
              width: '100%',
              boxShadow: '0 20px 45px rgba(15, 23, 42, 0.12), 0 4px 12px rgba(37, 99, 235, 0.06)',
              border: '1px solid #dbeafe',
            }}>
              <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '14px',
                  borderRadius: '20px',
                  background: 'linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%)',
                  boxShadow: '0 8px 18px rgba(37, 99, 235, 0.12)',
                  fontSize: '2rem',
                }}>📢</div>
                <h2 style={{ margin: 0, color: '#0f172a', fontSize: isMobile ? '1.35rem' : '1.55rem', fontWeight: '800', letterSpacing: '-0.02em' }}>
                  Create Broadcast
                </h2>
                <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.5, margin: '8px auto 0', maxWidth: '390px' }}>
                  Submit a message for admin approval.
                </p>
              </div>

              <form onSubmit={async (event) => {
                event.preventDefault();
                setBroadcastStatus(null);
                const mobile = registrationMobile.replace(/\D/g, '');
                if (!/^\d{10}$/.test(mobile) || !broadcastPlanId || !broadcastMessage.trim()
                  || !broadcastLatitude || !broadcastLongitude) {
                  setBroadcastStatus({ type: 'error', message: 'Registered mobile, broadcast type, message, and location are required.' });
                  return;
                }

                setBroadcastSubmitting(true);
                try {
                  const response = await apiClient.post('/v1/trust/broadcast/createBroadcast', {
                    mobile,
                    plan_id: Number(broadcastPlanId),
                    message: broadcastMessage.trim(),
                    lat: Number(broadcastLatitude),
                    lon: Number(broadcastLongitude),
                  });
                  setBroadcastStatus({
                    type: response.data?.status ? 'success' : 'error',
                    message: response.data?.msg || 'Broadcast request completed.',
                  });
                  if (response.data?.status) setBroadcastMessage('');
                } catch (error) {
                  setBroadcastStatus({
                    type: 'error',
                    message: error.response?.data?.msg || 'Failed to create broadcast.',
                  });
                } finally {
                  setBroadcastSubmitting(false);
                }
              }}>
                <label style={{ display: 'block', marginBottom: '24px', color: '#1e293b', fontSize: '0.9rem', fontWeight: '700' }}>
                  Registered mobile number
                  <input
                    type="tel"
                    value={registrationMobile}
                    readOnly
                    aria-readonly="true"
                    style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '8px', padding: '12px 14px', border: '1px solid #cbd5e1', borderRadius: '12px', backgroundColor: '#f8fafc', color: '#475569', fontSize: '0.95rem', cursor: 'not-allowed' }}
                  />
                </label>
                <fieldset style={{ border: 'none', padding: 0, margin: '0 0 24px' }}>
                  <legend style={{ marginBottom: '10px', color: '#1e293b', fontSize: '0.9rem', fontWeight: '700' }}>
                    Broadcast type
                  </legend>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                    {broadcastTypes.map(({ label, planId }) => {
                      const selected = broadcastType === label;
                      return (
                        <label
                          key={label}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 14px',
                            border: `1px solid ${selected ? '#2563eb' : '#dbe3ef'}`,
                            borderRadius: '12px',
                            background: selected ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' : '#ffffff',
                            color: selected ? '#1d4ed8' : '#334155',
                            fontSize: '0.875rem',
                            fontWeight: '700',
                            cursor: 'pointer',
                            boxShadow: selected ? '0 5px 12px rgba(37, 99, 235, 0.12)' : '0 2px 5px rgba(15, 23, 42, 0.04)',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            aria-label={label}
                            onChange={() => {
                              const nextType = selected ? '' : label;
                              setBroadcastType(nextType);
                              setBroadcastPlanId(nextType ? String(planId) : '');
                            }}
                          />
                          {label}
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'auto 1fr 1fr',
                  alignItems: 'end',
                  gap: '12px',
                  marginBottom: '24px',
                  padding: '14px',
                  border: '1px solid #dbeafe',
                  borderRadius: '14px',
                  backgroundColor: '#f8fbff',
                }}>
                  <button
                    type="button"
                    onClick={handleBroadcastLocation}
                    style={{ minHeight: '44px', padding: '10px 14px', border: '1px solid #93c5fd', borderRadius: '10px', background: 'linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)', color: '#1d4ed8', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 3px 8px rgba(37, 99, 235, 0.08)' }}
                  >
                    <span aria-hidden="true">📍</span> Get location
                  </button>
                  <label style={{ color: '#475569', fontSize: '0.75rem', fontWeight: '700' }}>
                    Latitude
                    <input
                      type="text"
                      value={broadcastLatitude}
                      readOnly
                      placeholder="Latitude"
                      style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '6px', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '9px', backgroundColor: '#ffffff', color: '#475569', fontFamily: 'monospace' }}
                    />
                  </label>
                  <label style={{ color: '#475569', fontSize: '0.75rem', fontWeight: '700' }}>
                    Longitude
                    <input
                      type="text"
                      value={broadcastLongitude}
                      readOnly
                      placeholder="Longitude"
                      style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '6px', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '9px', backgroundColor: '#ffffff', color: '#475569', fontFamily: 'monospace' }}
                    />
                  </label>
                </div>
                {broadcastLocationStatus && (
                  <p style={{ margin: '-14px 0 20px', color: '#64748b', fontSize: '0.75rem', textAlign: 'center' }}>
                    {broadcastLocationStatus}
                  </p>
                )}
                <label style={{ display: 'block', marginBottom: '22px', color: '#1e293b', fontSize: '0.9rem', fontWeight: '700' }}>
                  Message
                  <textarea
                    value={broadcastMessage}
                    onChange={(event) => setBroadcastMessage(event.target.value)}
                    maxLength={200}
                    rows={5}
                    placeholder="Write your broadcast message..."
                    style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '8px', padding: '12px 14px', border: '1px solid #cbd5e1', borderRadius: '12px', backgroundColor: '#ffffff', color: '#1e293b', fontSize: '0.9rem', lineHeight: 1.5, resize: 'vertical', outline: 'none' }}
                  />
                  <span style={{ display: 'block', marginTop: '6px', color: '#94a3b8', fontSize: '0.75rem', textAlign: 'right' }}>
                    {broadcastMessage.length}/200
                  </span>
                </label>
                {broadcastStatus && (
                  <p style={{ padding: '10px 12px', margin: '0 0 16px', borderRadius: '10px', backgroundColor: broadcastStatus.type === 'success' ? '#f0fdf4' : '#fef2f2', color: broadcastStatus.type === 'success' ? '#15803d' : '#b91c1c', fontSize: '0.85rem', fontWeight: '600', textAlign: 'center' }}>
                    {broadcastStatus.message}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={broadcastSubmitting}
                  style={{ width: '100%', padding: '13px 16px', border: 'none', borderRadius: '12px', background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', color: '#ffffff', fontSize: '0.95rem', fontWeight: '800', cursor: broadcastSubmitting ? 'not-allowed' : 'pointer', opacity: broadcastSubmitting ? 0.7 : 1, boxShadow: '0 8px 16px rgba(37, 99, 235, 0.24)', transition: 'all 0.2s ease' }}
                >
                  {broadcastSubmitting ? 'Submitting...' : 'Create Broadcast'}
                </button>
              </form>
            </div>
          )}

          {activeView === 'customer-support' && (
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              padding: isMobile ? '28px 20px' : '36px 36px',
              maxWidth: '540px',
              width: '100%',
              boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.08), 0 8px 10px -6px rgba(15, 23, 42, 0.04)',
              border: '1px solid #000000',
            }}>
              {!ticketMobileLocked ? (
                <div style={{ maxWidth: '400px', margin: '0 auto' }}>
                  <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>🎧</div>
                    <h2 style={{ margin: 0, color: '#0f172a', fontSize: '1.35rem' }}>
                      Verify your mobile number
                    </h2>
                    <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.5 }}>
                      A verified TrustApp mobile number is required before you can submit a support ticket.
                    </p>
                    {ticketMobileLookupError && (
                      <p style={{ color: '#64748b', fontSize: '0.85rem', lineHeight: 1.5 }}>
                        {ticketMobileLookupError}
                      </p>
                    )}
                  </div>

                  {ticketRegistrationStep === 'mobile' ? (
                    <form onSubmit={handleSendTicketRegistrationOtp}>
                      <label style={{ display: 'block', marginBottom: '18px', color: '#334155', fontSize: '0.85rem', fontWeight: '600' }}>
                        Mobile number
                        <input
                          type="tel"
                          inputMode="numeric"
                          maxLength={10}
                          value={ticketMobile}
                          onChange={(event) => setTicketMobile(event.target.value.replace(/\D/g, ''))}
                          placeholder="Enter 10-digit mobile number"
                          style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '6px', padding: '10px 12px', border: '1px solid #000000', borderRadius: '8px' }}
                        />
                      </label>
                      {ticketRegistrationStatus && (
                        <p style={{ color: ticketRegistrationStatus.type === 'success' ? '#15803d' : '#b91c1c', fontSize: '0.875rem' }}>
                          {ticketRegistrationStatus.message}
                        </p>
                      )}
                      <button
                        type="submit"
                        disabled={ticketRegistrationSubmitting}
                        style={{ width: '100%', padding: '11px', border: '1px solid #000000', borderRadius: '8px', backgroundColor: '#2563eb', color: '#ffffff', fontWeight: '600', cursor: ticketRegistrationSubmitting ? 'not-allowed' : 'pointer', opacity: ticketRegistrationSubmitting ? 0.7 : 1 }}
                      >
                        {ticketRegistrationSubmitting ? 'Sending OTP...' : 'Send OTP'}
                      </button>
                    </form>
                  ) : (
                    <form onSubmit={handleVerifyTicketRegistrationOtp}>
                      <label style={{ display: 'block', marginBottom: '18px', color: '#334155', fontSize: '0.85rem', fontWeight: '600' }}>
                        OTP
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          value={ticketRegistrationOtp}
                          onChange={(event) => setTicketRegistrationOtp(event.target.value.replace(/\D/g, ''))}
                          placeholder="Enter OTP"
                          style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '6px', padding: '10px 12px', border: '1px solid #000000', borderRadius: '8px' }}
                        />
                      </label>
                      {ticketRegistrationStatus && (
                        <p style={{ color: ticketRegistrationStatus.type === 'success' ? '#15803d' : '#b91c1c', fontSize: '0.875rem' }}>
                          {ticketRegistrationStatus.message}
                        </p>
                      )}
                      <button
                        type="submit"
                        disabled={ticketRegistrationSubmitting}
                        style={{ width: '100%', padding: '11px', border: '1px solid #000000', borderRadius: '8px', backgroundColor: '#2563eb', color: '#ffffff', fontWeight: '600', cursor: ticketRegistrationSubmitting ? 'not-allowed' : 'pointer', opacity: ticketRegistrationSubmitting ? 0.7 : 1 }}
                      >
                        {ticketRegistrationSubmitting ? 'Verifying...' : 'Verify and continue'}
                      </button>
                    </form>
                  )}
                </div>
              ) : ticketSubmitted ? (
                /* ── Success view ── */
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    backgroundColor: '#ecfdf5',
                    border: '1px solid #a7f3d0',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.75rem',
                    color: '#059669',
                    marginBottom: '16px',
                  }}>
                    ✓
                  </div>
                  <h2 style={{
                    color: '#0f172a',
                    fontSize: '1.4rem',
                    fontWeight: '700',
                    marginTop: 0,
                    marginBottom: '10px',
                    letterSpacing: '-0.02em',
                  }}>Ticket Submitted</h2>
                  <p style={{
                    color: '#64748b',
                    fontSize: '0.925rem',
                    lineHeight: '1.6',
                    marginBottom: '28px',
                  }}>
                    Your support ticket has been received. Our team will review your inquiry and get back to you shortly.
                  </p>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => {
                        if (!ticketMobileLocked) setTicketMobile('');
                        setTicketSubject('');
                        setTicketDetails('');
                        setTicketTopic('');
                        setTicketErrors({});
                        setTicketSubmitted(false);
                      }}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#2563eb',
                        color: '#ffffff',
                        border: '1px solid #000000',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                    >Submit Another Ticket</button>
                    <button
                      onClick={() => {
                        setTicketSubmitted(false);
                        if (!ticketMobileLocked) setTicketMobile('');
                        setTicketSubject('');
                        setTicketDetails('');
                        setTicketTopic('');
                        setTicketErrors({});
                        setActiveView('home');
                      }}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#ffffff',
                        color: '#0f172a',
                        border: '1px solid #000000',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8fafc';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#ffffff';
                      }}
                    >Back to Home</button>
                  </div>
                </div>
              ) : (
                /* ── Ticket form ── */
                <>
                  <div style={{
                    textAlign: 'center',
                    marginBottom: '28px',
                  }}>
                    <div style={{
                      display: 'inline-flex',
                      width: '48px',
                      height: '48px',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '12px',
                      backgroundColor: '#eff6ff',
                      border: '1px solid #000000',
                      color: '#2563eb',
                      fontSize: '1.4rem',
                      marginBottom: '12px',
                    }}>🎧</div>
                    <h2 style={{
                      margin: 0,
                      fontSize: isMobile ? '1.35rem' : '1.5rem',
                      fontWeight: '700',
                      color: '#0f172a',
                      letterSpacing: '-0.02em',
                    }}>Customer Support</h2>
                    <p style={{
                      color: '#64748b',
                      fontSize: '0.875rem',
                      marginTop: '6px',
                      marginBottom: 0,
                      fontWeight: '400',
                    }}>Submit a support ticket and our team will get back to you promptly.</p>
                  </div>

                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    const errs = {};
                    if (!ticketMobileLocked) errs.mobile = 'Verify your mobile number before submitting a support ticket.';
                    else if (!/^\d{10}$/.test(ticketMobile)) errs.mobile = 'Please enter a valid 10-digit mobile number';
                    if (!ticketTopic) errs.topic = 'Please select a topic';
                    if (!ticketSubject.trim()) errs.subject = 'Subject is required';
                    else if (ticketSubject.length > 20) errs.subject = 'Max 20 characters';
                    if (!ticketDetails.trim()) errs.details = 'Details are required';
                    else if (ticketDetails.length > 200) errs.details = 'Max 200 characters';
                    setTicketErrors(errs);
                    if (Object.keys(errs).length === 0) {
                      setTicketSubmitting(true);
                      try {
                        const htmlBody = `
                          <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #f8fafc; padding: 24px;">
                            <div style="background: #1e293b; padding: 24px; border-radius: 8px 8px 0 0; text-align: left;">
                              <h2 style="margin: 0; color: #ffffff; font-size: 20px; font-weight: 600; letter-spacing: -0.01em;">Customer Support Ticket</h2>
                              <p style="margin: 6px 0 0; color: #94a3b8; font-size: 13px;">New support inquiry submitted via TrustApp</p>
                            </div>

                            <div style="background: #ffffff; padding: 24px; border: 1px solid #e2e8f0; border-top: none; border-radius: 0 0 8px 8px;">
                              <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
                                <tr>
                                  <td style="padding: 12px 14px; background: #f8fafc; border: 1px solid #e2e8f0; font-weight: 600; color: #475569; width: 130px; font-size: 13px;">Mobile</td>
                                  <td style="padding: 12px 14px; border: 1px solid #e2e8f0; color: #0f172a; font-size: 14px; font-weight: 500;">${ticketMobile}</td>
                                </tr>
                                <tr>
                                  <td style="padding: 12px 14px; background: #f8fafc; border: 1px solid #e2e8f0; font-weight: 600; color: #475569; font-size: 13px;">Topic</td>
                                  <td style="padding: 12px 14px; border: 1px solid #e2e8f0; color: #0f172a; font-size: 14px;">${ticketTopic}</td>
                                </tr>
                                <tr>
                                  <td style="padding: 12px 14px; background: #f8fafc; border: 1px solid #e2e8f0; font-weight: 600; color: #475569; font-size: 13px;">Subject</td>
                                  <td style="padding: 12px 14px; border: 1px solid #e2e8f0; color: #0f172a; font-size: 14px; font-weight: 500;">${ticketSubject}</td>
                                </tr>
                                <tr>
                                  <td style="padding: 12px 14px; background: #f8fafc; border: 1px solid #e2e8f0; font-weight: 600; color: #475569; vertical-align: top; font-size: 13px;">Details</td>
                                  <td style="padding: 12px 14px; border: 1px solid #e2e8f0; color: #334155; font-size: 14px; line-height: 1.6;">${ticketDetails.replace(/\n/g, '<br/>')}</td>
                                </tr>
                              </table>

                              <div style="margin-top: 16px;">
                                <a href="tel:+91${ticketMobile}" style="display: inline-block; text-decoration: none; background: #2563eb; color: #ffffff; padding: 9px 18px; border-radius: 6px; font-size: 13px; font-weight: 600; margin-right: 10px;">Call Customer</a>
                                <a href="https://wa.me/91${ticketMobile}" style="display: inline-block; text-decoration: none; background: #16a34a; color: #ffffff; padding: 9px 18px; border-radius: 6px; font-size: 13px; font-weight: 600;">WhatsApp</a>
                              </div>
                            </div>
                          </div>
                        `;

                        await apiClient.post('/v1/trust/sendEmail', {
                          to: 'contact@cloudai.in',
                          subject: `[Support Ticket] ${ticketSubject}`,
                          phone: ticketMobile,
                          topic: ticketTopic,
                          message: `Mobile: ${ticketMobile}, Topic: ${ticketTopic}, Subject: ${ticketSubject}, Details: ${ticketDetails}`,
                          body: htmlBody,
                          email: userEmail,
                        });

                        setTicketSubmitted(true);
                      } catch (err) {
                        console.error('Email send error:', err);
                        setTicketErrors({ submit: 'Failed to send ticket. Please try again.' });
                      } finally {
                        setTicketSubmitting(false);
                      }
                    }
                  }} noValidate>

                    {/* Mobile Number */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#334155', marginBottom: '6px', letterSpacing: '0.01em' }}>Mobile Number</label>
                      <input
                        type="tel"
                        value={ticketMobile}
                        onChange={(e) => {
                          if (!ticketMobileLocked) {
                            const value = e.target.value.replace(/\D/g, '');
                            if (value.length <= 10) setTicketMobile(value);
                          }
                        }}
                        placeholder={ticketMobileLocked ? '' : 'Enter 10-digit mobile number'}
                        readOnly={ticketMobileLocked}
                        inputMode="numeric"
                        style={{
                          width: '100%',
                          padding: '11px 14px',
                          fontSize: '0.925rem',
                          color: '#0f172a',
                          backgroundColor: ticketMobileLocked ? '#f1f5f9' : '#ffffff',
                          border: `1px solid ${ticketErrors.mobile ? '#dc2626' : '#000000'}`,
                          borderRadius: '8px',
                          outline: 'none',
                          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                          fontFamily: 'Inter, sans-serif',
                          boxSizing: 'border-box',
                          boxShadow: ticketErrors.mobile ? '0 0 0 3px rgba(220, 38, 38, 0.1)' : 'none',
                        }}
                        onFocus={ticketMobileLocked ? undefined : (e) => {
                          e.currentTarget.style.borderColor = '#000000';
                          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 0, 0, 0.08)';
                        }}
                        onBlur={ticketMobileLocked ? undefined : (e) => {
                          e.currentTarget.style.borderColor = ticketErrors.mobile ? '#dc2626' : '#000000';
                          e.currentTarget.style.boxShadow = ticketErrors.mobile ? '0 0 0 3px rgba(220, 38, 38, 0.1)' : 'none';
                        }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {ticketErrors.mobile
                          ? <span style={{ color: '#dc2626', fontSize: '0.78rem', marginTop: '4px', fontWeight: '500' }}>{ticketErrors.mobile}</span>
                          : <span style={{ color: ticketMobileLocked ? '#047857' : '#64748b', fontSize: '0.78rem', marginTop: '4px', fontWeight: '500' }}>{ticketMobileLocked ? 'Verified mobile number linked to your account' : ticketMobileLookupError}</span>}
                        <span style={{ textAlign: 'right', fontSize: '0.75rem', color: ticketMobile.length > 10 ? '#dc2626' : '#94a3b8', marginTop: '4px', fontWeight: '500' }}>{ticketMobile.length}/10</span>
                      </div>
                    </div>

                    {/* Topic */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#334155', marginBottom: '8px', letterSpacing: '0.01em' }}>Topic</label>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
                        gap: '10px',
                      }}>
                        {[
                          { label: 'Rent', icon: '🏠' },
                          { label: 'Flat-Buy', icon: '🏢' },
                          { label: 'Flat-Sale', icon: '🏷️' },
                          { label: 'Distress sale', icon: '📉' },
                          { label: 'Other support', icon: '💬' },
                          { label: 'Any Delivery', icon: '🚚' },
                          { label: 'Trips', icon: '🛣️' },
                        ].map(({ label, icon }) => {
                          const isSelected = ticketTopic === label;
                          return (
                            <label
                              key={label}
                              onClick={() => setTicketTopic(label)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '10px 12px',
                                borderRadius: '8px',
                                border: '1px solid #000000',
                                backgroundColor: isSelected ? '#f1f5f9' : '#ffffff',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                fontSize: '0.85rem',
                                fontWeight: isSelected ? '600' : '500',
                                color: '#0f172a',
                                userSelect: 'none',
                                boxShadow: isSelected ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none',
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.backgroundColor = '#f8fafc';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.backgroundColor = '#ffffff';
                                }
                              }}
                            >
                              <span style={{
                                fontSize: '1rem',
                                width: '28px',
                                height: '28px',
                                borderRadius: '6px',
                                backgroundColor: isSelected ? '#e2e8f0' : '#f1f5f9',
                                border: '1px solid #000000',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}>{icon}</span>
                              <span style={{ flex: 1, wordBreak: 'break-word', lineHeight: '1.3' }}>{label}</span>
                              <span style={{
                                width: '18px',
                                height: '18px',
                                borderRadius: '4px',
                                border: '1px solid #000000',
                                backgroundColor: isSelected ? '#000000' : '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}>
                                {isSelected && (
                                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                    <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      {ticketErrors.topic && <span style={{ color: '#dc2626', fontSize: '0.78rem', marginTop: '6px', display: 'block', fontWeight: '500' }}>{ticketErrors.topic}</span>}
                    </div>

                    {/* Subject */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#334155', marginBottom: '6px', letterSpacing: '0.01em' }}>Subject</label>
                      <input
                        type="text"
                        value={ticketSubject}
                        onChange={(e) => { if (e.target.value.length <= 20) setTicketSubject(e.target.value); }}
                        placeholder="Brief summary of your inquiry"
                        maxLength={20}
                        style={{
                          width: '100%',
                          padding: '11px 14px',
                          fontSize: '0.925rem',
                          color: '#0f172a',
                          backgroundColor: '#ffffff',
                          border: `1px solid ${ticketErrors.subject ? '#dc2626' : '#000000'}`,
                          borderRadius: '8px',
                          outline: 'none',
                          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                          fontFamily: 'Inter, sans-serif',
                          boxSizing: 'border-box',
                          boxShadow: ticketErrors.subject ? '0 0 0 3px rgba(220, 38, 38, 0.1)' : 'none',
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = '#000000';
                          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 0, 0, 0.08)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = ticketErrors.subject ? '#dc2626' : '#000000';
                          e.currentTarget.style.boxShadow = ticketErrors.subject ? '0 0 0 3px rgba(220, 38, 38, 0.1)' : 'none';
                        }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {ticketErrors.subject ? <span style={{ color: '#dc2626', fontSize: '0.78rem', marginTop: '4px', fontWeight: '500' }}>{ticketErrors.subject}</span> : <span />}
                        <span style={{ textAlign: 'right', fontSize: '0.75rem', color: ticketSubject.length > 20 ? '#dc2626' : '#94a3b8', marginTop: '4px', fontWeight: '500' }}>{ticketSubject.length}/20</span>
                      </div>
                    </div>

                    {/* Ticket Details */}
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#334155', marginBottom: '6px', letterSpacing: '0.01em' }}>Ticket Details</label>
                      <textarea
                        value={ticketDetails}
                        onChange={(e) => { if (e.target.value.length <= 200) setTicketDetails(e.target.value); }}
                        placeholder="Please provide details about your request or issue..."
                        rows={4}
                        maxLength={200}
                        style={{
                          width: '100%',
                          padding: '11px 14px',
                          fontSize: '0.925rem',
                          color: '#0f172a',
                          backgroundColor: '#ffffff',
                          border: `1px solid ${ticketErrors.details ? '#dc2626' : '#000000'}`,
                          borderRadius: '8px',
                          outline: 'none',
                          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                          fontFamily: 'Inter, sans-serif',
                          boxSizing: 'border-box',
                          resize: 'vertical',
                          minHeight: '96px',
                          boxShadow: ticketErrors.details ? '0 0 0 3px rgba(220, 38, 38, 0.1)' : 'none',
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = '#000000';
                          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 0, 0, 0.08)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = ticketErrors.details ? '#dc2626' : '#000000';
                          e.currentTarget.style.boxShadow = ticketErrors.details ? '0 0 0 3px rgba(220, 38, 38, 0.1)' : 'none';
                        }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {ticketErrors.details ? <span style={{ color: '#dc2626', fontSize: '0.78rem', marginTop: '4px', fontWeight: '500' }}>{ticketErrors.details}</span> : <span />}
                        <span style={{ textAlign: 'right', fontSize: '0.75rem', color: ticketDetails.length > 200 ? '#dc2626' : '#94a3b8', marginTop: '4px', fontWeight: '500' }}>{ticketDetails.length}/200</span>
                      </div>
                    </div>

                    {/* Submit Error */}
                    {ticketErrors.submit && (
                      <div style={{
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: '#991b1b',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        marginBottom: '16px',
                        textAlign: 'center',
                      }}>
                        {ticketErrors.submit}
                      </div>
                    )}

                    {/* Buttons */}
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <button
                        type="submit"
                        disabled={ticketSubmitting}
                        style={{
                          flex: 1,
                          minWidth: '140px',
                          padding: '11px 20px',
                          backgroundColor: '#2563eb',
                          color: '#ffffff',
                          border: '1px solid #000000',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          cursor: ticketSubmitting ? 'not-allowed' : 'pointer',
                          transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
                          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                          opacity: ticketSubmitting ? 0.7 : 1,
                        }}
                        onMouseEnter={(e) => {
                          if (!ticketSubmitting) {
                            e.currentTarget.style.backgroundColor = '#1d4ed8';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.2)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!ticketSubmitting) {
                            e.currentTarget.style.backgroundColor = '#2563eb';
                            e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                          }
                        }}
                      >
                        {ticketSubmitting ? 'Sending...' : 'Submit Ticket'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (!ticketMobileLocked) setTicketMobile('');
                          setTicketSubject('');
                          setTicketDetails('');
                          setTicketTopic('');
                          setTicketErrors({});
                          setActiveView('home');
                        }}
                        style={{
                          flex: 1,
                          minWidth: '140px',
                          padding: '11px 20px',
                          backgroundColor: '#ffffff',
                          color: '#0f172a',
                          border: '1px solid #000000',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8fafc';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#ffffff';
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default UtilityDashboard;